import React, { useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import CustomFormatNumber from "components/CustomFormatNumber";
import PrevButton from "components/PrevButton";
import NextButton from "components/NextButton";
import { useFormattedMessage, useSideModalScrollToTop } from "hooks";
import { selectProjectDetailsForId } from "store/project/selectors";
import { selectFullInvestmentDetails } from "store/user/investment/selectors";
import { selectUserDetails } from "store/user/account/selectors";
import { selectTenant } from "store/app/selectors";

import { TENANTS_LIST, staticProjectRunningTimeInYears } from "constants/index";

const messages = {
    heading: "costs_and_grants",
    forInfo: "investment_free_of_charge",
    content1: "cost_schedule",
    content2: "investment_incurs_cost_benefits",
    issueAmount: "issue_amount",
    typeOfCosts: "type_of_costs",
    figuresIn: "figures_in",
    monetaryAllowances: "monetary_allowances",
    tiedAgent: "tied_agent",
    tipsterCommission: "tipster_commission",
    oneOffCosts: "one_off_costs",
    mediationCosts: "mediation_costs",
    thereOfGrants: "there_of_grants",
    financialInstrumentCosts: "financial_instrument_costs",
    totalCostFinancialInstrument: "total_cost_financial_instrument",
    runningCosts: "running_costs",
    totalCostsOverYearsText1: "total_costs",
    totalCostsOverYearsText2: "total_costs_over_years",
    totalCostsOver15Years: "total_costs_over_15years",
    totalCostsOver8Years: "total_costs_over_8years",
    totalCostsOver10Years: "total_costs_over_10years",
    totalCostsPA: "total_costs_pa",
    costsReduceFirstYear: "costs_reduce_first_year",
    costsReduceFollowingYears: "costs_reduce_following_years",
    tipsterCommissionNote: "tipster_commission_note",
};

const SingleRow = function ({ left, middle, right }) {
    return (
        <div className="item flex justify-between align-center">
            <span className="text-14 fw-600">{left}</span>
            <div className="cost flex justify-between align-center w-auto">
                <span className="text-12 fw-500">{middle} %</span>
                <span className="text-14 fw-600 ml-1">
                    <CustomFormatNumber value={right} />
                </span>
            </div>
        </div>
    );
};

const HeadingRow = function () {
    const { formatMessage } = useFormattedMessage();
    return (
        <div className="item flex justify-between align-center">
            <span className="text-14 fw-600">{formatMessage(messages.typeOfCosts)}</span>
            <span className="text-12 fw-500">{formatMessage(messages.figuresIn)}</span>
        </div>
    );
};

const Heading = function ({ heading, children }) {
    return (
        <div className="flex justify-between">
            <h6 className="my-0">{heading}</h6>
            {children}
        </div>
    );
};

const SingleBlock = function ({ heading, investSum, rows, lastBorder, afterRow }) {
    return (
        <div className="single-benefit mt-10">
            {heading && (
                <Heading heading={heading}>
                    {investSum && (
                        <span className="text-14 fw-600">
                            <CustomFormatNumber value={investSum} />
                        </span>
                    )}
                </Heading>
            )}
            <div className="border-x my-2" />
            <HeadingRow />
            {rows.map((row, index) => {
                return <SingleRow key={index} left={row.left} middle={row.middle} right={row.right} />;
            })}
            {lastBorder && <div className="border-x my-2" />}
            {afterRow && <SingleRow left={afterRow.left} middle={afterRow.middle} right={afterRow.right} />}
        </div>
    );
};

const InvestmentCostsBenefits = ({
    className = "",
    setInvestmentStepData,
    investmentStepData,
    costBenefits,
    costBenefitsValues,
    fullInvestmentDetails,
    projectData,
    backSlideEffect = "",
    setBackSlideEffect,
    user = {},
    currentTenant,
}) => {
    useSideModalScrollToTop();
    const { formatMessage } = useFormattedMessage();

    const [isSubmitButtonLoading, setSubmitButtonLaoding] = useState(false);

    const userTipster = user?.Tipster ?? {};
    const isShowTipsterCommission = currentTenant === TENANTS_LIST.SACHWERTINVEST && userTipster?.WasReferredByTheTipster !== "";
    const tipsterCommissionText = `${formatMessage(messages.tipsterCommission)} ${
        user?.Company?.CompanyName ? user?.Company?.CompanyName : `${fullInvestmentDetails?.data?.[0]?.TipsterFullName}`
    } *`;

    const runningTimeInYears =
        staticProjectRunningTimeInYears[projectData?.projectID] ||
        projectData?.financials?.costsAndBenefits?.runningTimeInYears ||
        15;

    const isInitInvestmentLoading = fullInvestmentDetails.isInitInvestmentLoading;
    const isInitInvestmentSuccess = fullInvestmentDetails.isInitInvestmentSuccess;

    const investSum = (investmentStepData.investSum && investmentStepData.investSum.sum) || 0;
    // const maximumFractionDigits = 2, minimumFractionDigits = 2

    const onSubmitCostsBenefits = () => {
        setSubmitButtonLaoding(true);

        setTimeout(() => {
            setSubmitButtonLaoding(false);
            setBackSlideEffect("step-slide-left");
            setInvestmentStepData({ investmentData: { step: "investmentOverviewStep" } });
        }, 2500);
    };

    return (
        <>
            <div className={`${className} costs-benefits ${backSlideEffect}`}>
                <h3>{formatMessage(messages.heading)}</h3>
                <span className="text-16 fw-600 d-block mb-10">{formatMessage(messages.forInfo)}</span>
                <p>{formatMessage(messages.content1)}</p>
                <p>{formatMessage(messages.content2)}</p>

                <SingleBlock
                    heading={formatMessage(messages.issueAmount)}
                    investSum={investSum}
                    rows={[
                        {
                            left: formatMessage(messages.monetaryAllowances),
                            middle: <CustomFormatNumber value={costBenefits.monetaryBenefitsToInstitute} noSymbol />,
                            right: costBenefitsValues.monetaryBenefitsToInstituteValue,
                        },
                        {
                            left: formatMessage(messages.tiedAgent),
                            middle: <CustomFormatNumber value={costBenefits.monetaryBenefitsToBroker} noSymbol />,
                            right: costBenefitsValues.monetaryBenefitsToBrokerValue,
                        },
                        ...(isShowTipsterCommission
                            ? [
                                  {
                                      left: tipsterCommissionText,
                                      middle: <CustomFormatNumber value={costBenefits.tipsterCommission} noSymbol />,
                                      right: costBenefitsValues.tipsterCommissionValue,
                                  },
                              ]
                            : []),
                    ]}
                />

                <SingleBlock
                    lastBorder
                    heading={formatMessage(messages.oneOffCosts)}
                    rows={[
                        {
                            left: formatMessage(messages.mediationCosts),
                            middle: <CustomFormatNumber value={costBenefits.oneTimePlacementFee} noSymbol />,
                            right: costBenefitsValues.oneTimePlacementFeeValue,
                        },
                        {
                            left: formatMessage(messages.thereOfGrants),
                            middle: <CustomFormatNumber value={costBenefits.oneTimeGrants} noSymbol />,
                            right: costBenefitsValues.oneTimeGrantsValue,
                        },
                        {
                            left: formatMessage(messages.financialInstrumentCosts),
                            middle: <CustomFormatNumber value={costBenefits.oneTimeCostsFi} noSymbol />,
                            right: costBenefitsValues.oneTimeCostsFiValue,
                        },
                    ]}
                    afterRow={{
                        left: formatMessage(messages.totalCostFinancialInstrument),
                        middle: <CustomFormatNumber value={costBenefits.oneTimeCosts} noSymbol />,
                        right: costBenefitsValues.oneTimeCostsValue,
                    }}
                />
                <SingleBlock
                    lastBorder
                    heading={formatMessage(messages.runningCosts)}
                    rows={[
                        {
                            left: formatMessage(messages.mediationCosts),
                            middle: <CustomFormatNumber value={costBenefits.runningPlacementFeePa} noSymbol />,
                            right: costBenefitsValues.runningPlacementFeePaValue,
                        },
                        {
                            left: formatMessage(messages.thereOfGrants),
                            middle: <CustomFormatNumber value={costBenefits.runningGrantsPa} noSymbol />,
                            right: costBenefitsValues.runningGrantsPaValue,
                        },
                        {
                            left: formatMessage(messages.financialInstrumentCosts),
                            middle: <CustomFormatNumber value={costBenefits.runningCostsFiPa} noSymbol />,
                            right: costBenefitsValues.runningCostsFiPaValue,
                        },
                    ]}
                    afterRow={{
                        left: formatMessage(messages.totalCostFinancialInstrument),
                        middle: <CustomFormatNumber value={costBenefits.costPerYear} noSymbol />,
                        right: costBenefitsValues.costPerYearValue,
                    }}
                />
                <SingleBlock
                    lastBorder
                    rows={[
                        {
                            left: `${formatMessage(messages.totalCostsOverYearsText1)}${runningTimeInYears}${formatMessage(
                                messages.totalCostsOverYearsText2,
                            )}`,
                            middle: <CustomFormatNumber value={costBenefits.value1} noSymbol />,
                            right: costBenefitsValues.value1,
                        },
                        {
                            left: formatMessage(messages.totalCostsPA),
                            middle: <CustomFormatNumber value={costBenefits.value2} noSymbol />,
                            right: costBenefitsValues.value2,
                        },
                        {
                            left: formatMessage(messages.costsReduceFirstYear),
                            middle: <CustomFormatNumber value={costBenefits.value3} noSymbol />,
                            right: costBenefitsValues.value3,
                        },
                        {
                            left: formatMessage(messages.costsReduceFollowingYears),
                            middle: <CustomFormatNumber value={costBenefits.value4} noSymbol />,
                            right: costBenefitsValues.value4,
                        },
                    ]}
                />
            </div>
            {isShowTipsterCommission && (
                <p className="mt-8">
                    <b>*</b> {formatMessage(messages.tipsterCommissionNote)}
                </p>
            )}
            <div className="bottom-bar-container">
                <PrevButton
                    onClick={() => {
                        // setBackSlideEffect("step-slide-right");
                        setInvestmentStepData({ investmentData: { step: "renderAdequacyStep" } });
                    }}
                />
                <NextButton
                    disable={(isInitInvestmentLoading && isInitInvestmentSuccess === false) || isSubmitButtonLoading}
                    className={isInitInvestmentLoading ? "disable-grey" : ""}
                    isLoading={isInitInvestmentLoading || isSubmitButtonLoading}
                    onClick={onSubmitCostsBenefits}
                />
            </div>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    fullInvestmentDetails: selectFullInvestmentDetails,
    projectData: selectProjectDetailsForId,
    user: selectUserDetails,
    currentTenant: selectTenant,
});

export default connect(mapStateToProps)(InvestmentCostsBenefits);
